import { Button, Container, Typography } from '@app/components';
import { ContentLoader } from '@app/components/loaders/content-loader/ContentLoader';
import { EmailApi } from '@app/domain/email/api/EmailApi';
import { FormControlLabel, Switch } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { useQueryParam, withDefault, StringParam } from 'use-query-params';

export const UnsubscribeEmailPage: React.FC = () => {
  const { t } = useTranslation();
  const [token] = useQueryParam('token', withDefault(StringParam, null));

  const infoQuery = useQuery({
    queryFn: async () => {
      if (!token) {
        return;
      }
      return EmailApi.getEmailPreferences(token);
    },
    onSuccess: (data) => {
      if (!data) {
        return;
      }
      setStatePreferences({
        qrNotification: data.qrNotification,
        trialLifecycle: data.trialLifecycle,
        tips: data.tips,
      });
    },
    refetchOnWindowFocus: false,
  });
  const savedPreference = infoQuery.data;

  const [statePreferences, setStatePreferences] = useState({
    qrNotification: savedPreference?.qrNotification ?? true,
    trialLifecycle: savedPreference?.trialLifecycle ?? true,
    tips: savedPreference?.tips ?? true,
  });

  const handleEmailPreferences = (key: keyof typeof statePreferences, value: boolean) => {
    setStatePreferences((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const savePreferences = useMutation({
    mutationFn: async () => {
      if (!token) {
        return;
      }
      return EmailApi.saveEmailPreferences({
        token,
        ...statePreferences,
      });
    },
    onSuccess: () => {
      toast.success(t('toaster.changesSaved'));
    },
  });

  const turnEverythingOff = () => {
    const allOff = { qrNotification: false, trialLifecycle: false, tips: false };
    setStatePreferences(allOff);
    if (!token) {
      return;
    }
    savePreferences.mutate();
  };

  return (
    <div className="flex flex-col items-center justify-center pt-16">
      {!token && <div>Invalid token. Check the link.</div>}
      {infoQuery.isLoading && <ContentLoader loading entity="Email preferences" isEmpty={false} />}
      {savedPreference && (
        <div>
          <div className="mb-8 rounded bg-gray-200 p-2 text-center text-gray-600 ring-1 ring-gray-400">
            {savedPreference.email}
          </div>
          <Container className="flex flex-col gap-6 p-6 lg:rounded-xl">
            <Typography variant="m" className="font-bold">
              {t('account.sectionEmailPreferences.title')}
            </Typography>
            <div className="flex flex-col gap-4">
              <div>
                <FormControlLabel
                  disabled
                  checked
                  control={<Switch />}
                  label={t('account.sectionEmailPreferences.accountNotifications')}
                />
                <Typography variant="s" className="ml-14 -mt-1 text-gray-500">
                  {t('account.sectionEmailPreferences.accountNotificationsHint')}
                </Typography>
              </div>
              <div>
                <FormControlLabel
                  onChange={() => {
                    handleEmailPreferences('qrNotification', !statePreferences.qrNotification);
                  }}
                  checked={statePreferences.qrNotification}
                  control={<Switch />}
                  label={t('account.sectionEmailPreferences.qrNotifications')}
                />
              </div>
              <div>
                <FormControlLabel
                  onChange={() => {
                    handleEmailPreferences('trialLifecycle', !statePreferences.trialLifecycle);
                  }}
                  checked={statePreferences.trialLifecycle}
                  control={<Switch />}
                  label={t('account.sectionEmailPreferences.trialLifecycle')}
                />
              </div>
              <div>
                <FormControlLabel
                  onChange={() => {
                    handleEmailPreferences('tips', !statePreferences.tips);
                  }}
                  checked={statePreferences.tips}
                  control={<Switch />}
                  label={t('account.sectionEmailPreferences.tips')}
                />
              </div>
            </div>
            <div className="flex flex-col gap-3 lg:flex-row">
              <Button
                onClick={() => {
                  savePreferences.mutate();
                }}
                color="secondary"
                variant="outlined"
                className="w-[220px]"
                loading={savePreferences.isLoading}
              >
                {t('dialog.saveChanges')}
              </Button>
              <Button onClick={turnEverythingOff} color="secondary" variant="text" className="w-[220px]">
                {t('account.sectionEmailPreferences.unsubscribeAll')}
              </Button>
            </div>
          </Container>
        </div>
      )}
    </div>
  );
};
